import React, { useState, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'

import { useApiClient } from '../../hooks/useApiClient'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../context/AuthContext'
import { formatDateDisplay } from '../../utils/dateHelpers'

interface Tarifa {
  id: string
  nombre: string
  tarifa_simple: number
  tarifa_multiple?: number
  fecha_inicio: string
  fecha_fin: string
  dias_semana: number[] | null
  activa: boolean
}

interface TarifasManagerProps {
  tipoEspacioId: string | null
  unidad_reserva?: string
  onTarifasLoaded?: (tarifas: Tarifa[]) => void
}

const TarifasManager: React.FC<TarifasManagerProps> = ({ tipoEspacioId, unidad_reserva = 'noche', onTarifasLoaded }) => {
  const { apiClient } = useApiClient()
  const { colors } = useTheme()
  const { permissions } = useAuth()
  const [tarifas, setTarifas] = useState<Tarifa[]>([])

  const hasPermission = (permission: string): boolean => {
    return permissions?.includes(permission) || false
  }
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [tarifaAEliminar, setTarifaAEliminar] = useState<string | null>(null)
  const [form, setForm] = useState({
    nombre: '',
    tarifa_simple: 0,
    tarifa_multiple: undefined as number | undefined,
    fecha_inicio: '',
    fecha_fin: '',
    dias_semana: [] as number[]
  })

  // Cargar tarifas cuando cambia el tipoEspacioId
  useEffect(() => {
    if (tipoEspacioId) {
      loadTarifas()
    } else {
      setTarifas([])
    }
  }, [tipoEspacioId])

  const loadTarifas = async () => {
    if (!tipoEspacioId) return

    setLoading(true)
    try {
      const response = await apiClient.get(`/api/admin-protected/tipos-espacios/${tipoEspacioId}/tarifas`)
      const data = response.data || []
      setTarifas(Array.isArray(data) ? data : [])
      onTarifasLoaded?.(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading tarifas:', error)
      setTarifas([])
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm({
      nombre: '',
      tarifa_simple: 0,
      tarifa_multiple: undefined,
      fecha_inicio: '',
      fecha_fin: '',
      dias_semana: []
    })
    setEditingId(null)
  }

  const handleNew = () => {
    if (!hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para crear tarifas')
      return
    }
    resetForm()
    setShowForm(true)
  }

  const handleEdit = (tarifa: Tarifa) => {
    if (!hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para editar tarifas')
      return
    }
    setForm({
      nombre: tarifa.nombre,
      tarifa_simple: tarifa.tarifa_simple,
      tarifa_multiple: tarifa.tarifa_multiple,
      fecha_inicio: tarifa.fecha_inicio,
      fecha_fin: tarifa.fecha_fin,
      dias_semana: tarifa.dias_semana || []
    })
    setEditingId(tarifa.id)
    setShowForm(true)
  }

  const toggleDiasSemana = (dia: number) => {
    setForm(prev => ({
      ...prev,
      dias_semana: prev.dias_semana.includes(dia)
        ? prev.dias_semana.filter(d => d !== dia)
        : [...prev.dias_semana, dia].sort()
    }))
  }

  const handleSubmit = async () => {
    if (!hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para guardar tarifas')
      return
    }

    if (!tipoEspacioId) {
      toastError('Debe guardar el tipo de espacio primero')
      return
    }

    if (!form.nombre.trim()) {
      toastError('El nombre de la tarifa es requerido')
      return
    }

    if (form.tarifa_simple <= 0) {
      toastError('La tarifa simple debe ser mayor a 0')
      return
    }

    if (!form.fecha_inicio || !form.fecha_fin) {
      toastError('Las fechas son requeridas')
      return
    }

    setLoading(true)
    try {
      const payload = {
        nombre: form.nombre,
        tarifa_simple: form.tarifa_simple,
        tarifa_multiple: form.tarifa_multiple && form.tarifa_multiple > 0 ? form.tarifa_multiple : undefined,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin,
        dias_semana: form.dias_semana.length > 0 ? form.dias_semana : null
      }

      if (editingId) {
        await apiClient.put(`/api/admin-protected/tipos-espacios/${tipoEspacioId}/tarifas/${editingId}`, payload)
        toastSuccess('Tarifa actualizada')
      } else {
        await apiClient.post(`/api/admin-protected/tipos-espacios/${tipoEspacioId}/tarifas`, payload)
        toastSuccess('Tarifa creada')
      }

      setShowForm(false)
      resetForm()
      // Pequeño delay para asegurar que el backend procesó
      await new Promise(resolve => setTimeout(resolve, 300))
      await loadTarifas()
    } catch (error) {
      console.error('Error saving tarifa:', error)
      toastError('Error al guardar la tarifa')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para eliminar tarifas')
      return
    }
    setTarifaAEliminar(id)
  }

  const confirmDelete = async () => {
    if (!hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para eliminar tarifas')
      return
    }

    if (!tipoEspacioId || !tarifaAEliminar) return

    setLoading(true)
    try {
      await apiClient.delete(`/api/admin-protected/tipos-espacios/${tipoEspacioId}/tarifas/${tarifaAEliminar}`)
      toastSuccess('Tarifa eliminada')
      setTarifaAEliminar(null)
      await loadTarifas()
    } catch (error) {
      console.error('Error deleting tarifa:', error)
      toastError('Error al eliminar la tarifa')
    } finally {
      setLoading(false)
    }
  }

  const cancelDelete = () => {
    setTarifaAEliminar(null)
  }

  const diasSemanaLabels = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']
  const esFinDeSemana = form.dias_semana.length === 2 && form.dias_semana.includes(5) && form.dias_semana.includes(6)
  const esSemanaLaboral = form.dias_semana.length === 5 && [0, 1, 2, 3, 4].every(d => form.dias_semana.includes(d))

  if (!tipoEspacioId) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
        <p className="text-sm text-gray-600">Guarda el tipo de espacio primero para gestionar tarifas</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tarifas</h3>
          <p className="text-sm text-gray-600">Gestiona las tarifas con fechas y días específicos</p>
        </div>
        {hasPermission('tipo_espacio.actualizar') && (
          <button
            type="button"
            onClick={handleNew}
            className="px-4 py-2 rounded-lg font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            ➕ Nueva Tarifa
          </button>
        )}
      </div>

      {/* Lista de Tarifas */}
      <div>
        {tarifas.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-600 mb-2">No hay tarifas configuradas</p>
            <p className="text-sm text-gray-500">Las tarifas sin especificar usarán la tarifa base</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tarifas.map((tarifa) => (
              <div
                key={tarifa.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow hover:border-gray-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{tarifa.nombre}</h4>
                    {!tarifa.activa && (
                      <span className="inline-block px-2 py-1 mt-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                        Inactiva
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {hasPermission('tipo_espacio.actualizar') && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEdit(tarifa)}
                          className="p-2 text-lg rounded-lg font-semibold transition-all duration-300 hover:bg-gray-100"
                          title="Editar tarifa"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(tarifa.id)}
                          className="p-2 text-lg rounded-lg font-semibold transition-all duration-300 hover:bg-red-50"
                          title="Eliminar tarifa"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-gray-600 text-xs">Tarifa</p>
                    <p className="font-semibold text-gray-900">
                      ${tarifa.tarifa_simple.toLocaleString()}{tarifa.tarifa_multiple ? `/${tarifa.tarifa_multiple.toLocaleString()}` : ''}
                      /{unidad_reserva === 'dia' ? 'día' : 'noche'}
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-600 text-xs">Período</p>
                    <p className="font-semibold text-gray-900">
                      {formatDateDisplay(tarifa.fecha_inicio)} - {formatDateDisplay(tarifa.fecha_fin)}
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-600 text-xs">Días</p>
                    <p className="font-semibold text-gray-900">
                      {tarifa.dias_semana
                        ? tarifa.dias_semana.map(d => diasSemanaLabels[d]).join(', ')
                        : 'Todos los días'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {editingId ? 'Editar Tarifa' : 'Nueva Tarifa'}
                </h2>
                <p className="text-gray-600 text-sm">
                  {editingId ? 'Actualiza la tarifa' : 'Crea una nueva tarifa para este tipo de espacio'}
                </p>
              </div>

              <div className="space-y-4">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Tarifa *</label>
                  <input
                    type="text"
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: Fin de Semana, Temporada Alta"
                  />
                </div>

                {/* Tarifa Simple */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tarifa Simple (1 {unidad_reserva === 'dia' ? 'Día' : 'Noche'}) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={form.tarifa_simple}
                    onChange={(e) => setForm({ ...form, tarifa_simple: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="50000"
                  />
                </div>

                {/* Tarifa Múltiple */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tarifa Múltiple (2+ {unidad_reserva === 'dia' ? 'Días' : 'Noches'}) (Opcional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.tarifa_multiple || ''}
                    onChange={(e) => setForm({ ...form, tarifa_multiple: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="40000"
                  />
                  <p className="text-xs text-gray-500 mt-1">Si no se especifica, se usa la tarifa simple</p>
                </div>

                {/* Fechas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio *</label>
                    <input
                      type="date"
                      required
                      value={form.fecha_inicio}
                      onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin *</label>
                    <input
                      type="date"
                      required
                      value={form.fecha_fin}
                      onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Días de la Semana */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Días de la Semana</label>
                  <p className="text-xs text-gray-600 mb-3">Deja vacío para aplicar todos los días</p>

                  {/* Atajos */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, dias_semana: [5, 6] })}
                      className={`px-3 py-2 text-xs rounded-lg font-semibold transition-all ${
                        esFinDeSemana
                          ? 'bg-blue-100 text-blue-900'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Fin de Semana
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, dias_semana: [0, 1, 2, 3, 4] })}
                      className={`px-3 py-2 text-xs rounded-lg font-semibold transition-all ${
                        esSemanaLaboral
                          ? 'bg-blue-100 text-blue-900'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Semana Laboral
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, dias_semana: [] })}
                      className={`px-3 py-2 text-xs rounded-lg font-semibold transition-all ${
                        form.dias_semana.length === 0
                          ? 'bg-blue-100 text-blue-900'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Todos los Días
                    </button>
                  </div>

                  {/* Selector individual */}
                  <div className="grid grid-cols-7 gap-2">
                    {diasSemanaLabels.map((label, index) => (
                      <label key={index} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={form.dias_semana.includes(index)}
                          onChange={() => toggleDiasSemana(index)}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: colors.primary }}
                        />
                        <span className="ml-1 text-xs text-gray-700">{label.substring(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false)
                    resetForm()
                  }}
                  className="px-6 py-2 text-base rounded-lg font-semibold transition-all duration-300"
                  style={{
                    backgroundColor: '#f3f4f6',
                    color: colors.primary,
                    border: `1px solid #d1d5db`
                  }}
                >
                  ❌ Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 text-base rounded-lg font-semibold text-white transition-all disabled:opacity-50"
                  style={{
                    backgroundColor: colors.primary
                  }}
                >
                  {loading ? '💾 Guardando...' : editingId ? '✏️ Actualizar' : '➕ Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {tarifaAEliminar && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="p-6 bg-red-50 border-b border-red-200">
              <div className="flex items-center gap-3">
                <div className="text-3xl">🗑️</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Eliminar Tarifa</h3>
                  <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                ¿Estás seguro de que deseas eliminar esta tarifa? Se perderán todos los datos asociados.
              </p>
              {tarifas.find(t => t.id === tarifaAEliminar) && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm font-semibold text-gray-900">
                    {tarifas.find(t => t.id === tarifaAEliminar)?.nombre}
                  </p>
                  <p className="text-sm text-gray-600">
                    ${tarifas.find(t => t.id === tarifaAEliminar)?.tarifa_simple.toLocaleString()}{tarifas.find(t => t.id === tarifaAEliminar)?.tarifa_multiple ? `/${tarifas.find(t => t.id === tarifaAEliminar)?.tarifa_multiple.toLocaleString()}` : ''}
                    /{unidad_reserva === 'dia' ? 'día' : 'noche'}
                  </p>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg font-semibold transition-all"
                style={{
                  backgroundColor: '#f3f4f6',
                  color: colors.primary,
                  border: `1px solid #d1d5db`
                }}
              >
                ❌ Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg font-semibold text-white transition-all disabled:opacity-50"
                style={{
                  backgroundColor: '#ef4444'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.backgroundColor = '#dc2626'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ef4444'
                }}
              >
                {loading ? '⏳ Eliminando...' : '🗑️ Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TarifasManager
